import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, GitBranch, Printer, RefreshCw, Volume2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { useAction } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import {
  buildInboundRoutingReport,
  downloadHtml,
  greetingKey,
  greetingRequest,
  openPrintableHtml,
  quoteSaid,
  renderInboundRoutingHtml,
  type DestinationBranch,
  type InboundRoutingReport as ReportData,
  type IvrGreeting,
} from '@/services/configReports';

type Tone = 'office' | 'after' | 'holiday' | 'unknown';

/** Greetings by IVR id; null while they are being transcribed. */
type Greetings = Map<string, IvrGreeting> | null;

function GreetingBox({ greeting, loading }: { greeting?: IvrGreeting; loading: boolean }) {
  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
        <Volume2 className="h-3.5 w-3.5 animate-pulse" /> Listening to the greeting…
      </div>
    );
  }
  if (!greeting) return null;
  const check = greeting.check;
  const source =
    greeting.source === 'prompt'
      ? 'From the greeting file on the PBX'
      : greeting.source === 'recording'
        ? `From a recorded call on ${greeting.recordedAt || 'a recent day'}`
        : '';
  return (
    <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 text-xs">
      <div className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-200">
        <Volume2 className="h-3.5 w-3.5" /> What callers hear
      </div>
      {greeting.transcript ? (
        <p className="mt-1.5 italic text-gray-700 dark:text-gray-300">“{greeting.transcript}”</p>
      ) : null}
      {greeting.message ? (
        <p className="mt-1.5 text-gray-500 dark:text-gray-400">{greeting.message}</p>
      ) : null}
      {source ? <p className="mt-1 text-[11px] text-gray-400">{source}</p> : null}
      {check ? (
        <div
          className={`mt-2 rounded-md px-2 py-1.5 ${
            check.ok
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300'
              : 'bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200'
          }`}
        >
          <div className="flex items-center gap-1.5 font-semibold">
            {check.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {check.ok ? 'The keys match the greeting' : 'Check this menu'}
          </div>
          {[...check.keys.filter((k) => !k.ok && k.note).map((k) => `Key ${k.key}: ${k.note}`), ...check.notes].map(
            (line) => (
              <div key={line} className="mt-1">
                {line}
              </div>
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

function DestTag({ kind }: { kind: string }) {
  const isQueue = kind === 'queue';
  const isIvr = kind === 'ivr';
  return (
    <span
      className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded mr-1.5 text-white ${
        isQueue
          ? 'bg-primary-700'
          : isIvr
            ? 'bg-teal-700'
            : kind === 'extension'
              ? 'bg-violet-700'
              : 'bg-slate-600'
      }`}
    >
      {kind === 'other' ? 'DEST' : kind.replace('_', ' ')}
    </span>
  );
}

function BranchPanel({
  branch,
  tone,
  greetings,
}: {
  branch: DestinationBranch;
  tone: Tone;
  greetings?: Greetings;
}) {
  const key = greetingKey(branch);
  const greeting = key ? greetings?.get(key) : undefined;
  const saidByKey = new Map((greeting?.check?.keys || []).map((k) => [k.key, k]));
  const border =
    tone === 'office'
      ? 'border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/20'
      : tone === 'unknown'
        ? 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/40'
        : 'border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20';
  const titleColor =
    tone === 'office'
      ? 'text-teal-800 dark:text-teal-300'
      : tone === 'unknown'
        ? 'text-gray-600 dark:text-gray-300'
        : 'text-amber-800 dark:text-amber-300';
  const boxBorder =
    tone === 'office'
      ? 'border-teal-300 dark:border-teal-700 text-teal-900 dark:text-teal-200'
      : tone === 'unknown'
        ? 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
        : 'border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200';
  const keyBg = tone === 'office' ? 'bg-teal-700' : 'bg-amber-700';

  return (
    <div className={`rounded-xl border ${border} p-4`}>
      <div className={`text-[11px] font-bold uppercase tracking-wide ${titleColor} text-center mb-2`}>
        {branch.title}
        {branch.live && (
          <span className="ml-2 inline-block rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] text-white normal-case tracking-normal">
            Live now
          </span>
        )}
        {branch.fromHistory && (
          <span className="ml-2 inline-block rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-900 dark:text-amber-200 normal-case tracking-normal">
            From call history
          </span>
        )}
      </div>
      <div
        className={`text-center rounded-lg border bg-white dark:bg-gray-900 py-2.5 px-3 text-sm font-semibold ${boxBorder}`}
      >
        <DestTag kind={branch.dest.kind} />
        {branch.dest.label}
        {branch.ivr && (
          <span className="block text-xs font-medium opacity-70 mt-1">
            IVR {branch.ivr.number || branch.ivr.id} · {branch.ivr.name}
          </span>
        )}
        {!branch.ivr && !branch.unknown && (
          <span className="block text-xs font-medium opacity-70 mt-1">
            {[branch.dest.type, branch.dest.value].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
      {branch.note && (
        <p className="mt-1.5 text-center text-[11px] text-gray-500 dark:text-gray-400">
          {branch.note}
        </p>
      )}

      {key && greetings !== undefined && (
        <GreetingBox greeting={greeting} loading={greetings === null} />
      )}

      {branch.ivr?.menuResolved && branch.ivr.menuResolved.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className={`text-[11px] font-bold uppercase tracking-wide ${titleColor}`}>
            IVR menu
          </div>
          {branch.ivr.menuResolved.map((opt) => (
            <div key={opt.key} className="flex gap-2">
              <div
                className={`w-8 shrink-0 rounded-md ${keyBg} text-white text-sm font-bold flex items-center justify-center`}
              >
                {opt.key}
              </div>
              <div className="flex-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm">
                <DestTag
                  kind={
                    opt.destType.toLowerCase().includes('queue')
                      ? 'queue'
                      : opt.destType.toLowerCase().includes('ext')
                        ? 'extension'
                        : 'other'
                  }
                />
                <span className="font-medium text-gray-900 dark:text-white">
                  {opt.resolvedLabel}
                </span>
                {saidByKey.get(opt.key)?.said ? (
                  <div
                    className={`mt-0.5 text-[11px] ${
                      saidByKey.get(opt.key)!.ok
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-amber-700 dark:text-amber-400'
                    }`}
                  >
                    {saidByKey.get(opt.key)!.ok ? '✓' : '⚠'} Greeting: {quoteSaid(saidByKey.get(opt.key)!.said)}
                  </div>
                ) : null}
                {opt.queueDetail ? (
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
                    {opt.queueDetail.agents?.length ? (
                      <div>
                        {opt.queueDetail.agents
                          .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
                          .join(', ')}
                      </div>
                    ) : null}
                    <div>
                      {opt.queueDetail.ringStrategy || 'Ring'}
                      {opt.queueDetail.maxWaitTime != null
                        ? ` · up to ${opt.queueDetail.maxWaitTime}s`
                        : ''}
                      {opt.queueDetail.failDestLabel || opt.queueDetail.failDestType
                        ? ` · failover → ${
                            opt.queueDetail.failDestLabel ||
                            [opt.queueDetail.failDestType, opt.queueDetail.failDestValue]
                              .filter(Boolean)
                              .join(' ')
                          }`
                        : ''}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          {branch.ivr.timeoutLabel && (
            <div className="text-xs rounded-lg border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/80 dark:bg-amber-900/20 px-3 py-2 text-amber-900 dark:text-amber-200">
              No-input / timeout → {branch.ivr.timeoutLabel}
            </div>
          )}
        </div>
      )}

      {branch.queue && (
        <div className="mt-3 text-xs text-gray-600 dark:text-gray-300 space-y-1">
          <div className="inline-block bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-200 rounded px-2 py-1">
            <b>{branch.queue.ringStrategy || 'Ring strategy n/a'}</b>
            {branch.queue.maxWaitTime != null ? ` · up to ${branch.queue.maxWaitTime} sec` : ''}
            {branch.queue.failDestLabel || branch.queue.failDestType
              ? ` · failover → ${
                  branch.queue.failDestLabel ||
                  [branch.queue.failDestType, branch.queue.failDestValue]
                    .filter(Boolean)
                    .join(' ')
                }`
              : ''}
          </div>
          <div>
            Agents:{' '}
            {branch.queue.agents.length
              ? branch.queue.agents
                  .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
                  .join(', ')
              : 'None listed'}
          </div>
        </div>
      )}
    </div>
  );
}

export function InboundRoutingReport() {
  const { session } = useModuleAccess('reporting');
  const customerName = session?.customer?.name || 'Customer';
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportData | null>(null);
  // undefined: not asked for; null: being transcribed
  const [greetings, setGreetings] = useState<Greetings | undefined>(undefined);
  const transcribeGreetings = useAction(backendApi.ivrPrompts.transcribeGreetings);

  const loadGreetings = async (data: ReportData, refresh = false) => {
    const ivrs = greetingRequest(data);
    if (!ivrs.length) {
      setGreetings(undefined);
      return;
    }
    setGreetings(null);
    try {
      const rows = (await transcribeGreetings({ ivrs, businessName: customerName, refresh })) as IvrGreeting[];
      setGreetings(new Map(rows.map((row) => [row.ivrId, row])));
    } catch (error) {
      console.error(error);
      setGreetings(undefined);
      toast.error(error instanceof Error ? error.message : 'Could not transcribe the IVR greetings');
    }
  };

  const generate = async () => {
    setLoading(true);
    setGreetings(undefined);
    try {
      const data = await buildInboundRoutingReport(customerName);
      setReport(data);
      toast.success(`Loaded ${data.cards.length} inbound route(s)`);
      void loadGreetings(data);
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to build inbound routing report'
      );
    } finally {
      setLoading(false);
    }
  };

  const html = useMemo(
    () => (report ? renderInboundRoutingHtml(report, greetings || undefined) : ''),
    [report, greetings]
  );

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
              <GitBranch className="w-6 h-6 text-indigo-700 dark:text-indigo-300" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Inbound Routing Report
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Live trunk → time condition → IVR/queue/extension flow for{' '}
                {customerName}&apos;s PBX.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void generate()} isLoading={loading} size="sm">
              <RefreshCw className="w-4 h-4 mr-1.5" />
              {report ? 'Refresh' : 'Generate'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report || greetings === null}
              title="Listen to every IVR greeting again, e.g. after a new recording was uploaded"
              onClick={() => {
                if (report) void loadGreetings(report, true);
              }}
            >
              <Volume2 className="w-4 h-4 mr-1.5" />
              {greetings === null ? 'Listening…' : 'Re-check greetings'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report}
              onClick={() => {
                if (!report) return;
                downloadHtml(
                  `Inbound_Routing_${customerName.replace(/\s+/g, '_')}.html`,
                  html
                );
                toast.success('HTML downloaded');
              }}
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download HTML
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report}
              onClick={() => {
                if (!html) return;
                openPrintableHtml(html);
              }}
            >
              <Printer className="w-4 h-4 mr-1.5" />
              Print / PDF
            </Button>
          </div>
        </div>
      </Card>

      {loading && (
        <Card>
          <Loader text="Loading routes, IVR menus, queues, and trunks from PBX..." />
        </Card>
      )}

      {!loading && report && (
        <>
          <Card>
            <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-gray-900 inline-block" /> Incoming
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-indigo-500 inline-block" /> Time condition
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-teal-700 inline-block" /> Office hours
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-amber-700 inline-block" /> After hours /
                holidays
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-primary-700 inline-block" /> Queue
              </span>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Generated {new Date(report.generatedAt).toLocaleString()} · {report.cards.length}{' '}
              route(s) · {report.ivrs.length} IVR(s) · {report.queues.length} queue(s) · each route
              hydrated via inbound_route/get
            </p>
          </Card>

          {report.cards.length === 0 ? (
            <Card>
              <p className="text-center text-gray-500 dark:text-gray-400 py-10">
                No inbound routes found on this PBX.
              </p>
            </Card>
          ) : (
            report.cards.map((card) => {
              const branches: Array<{ branch: DestinationBranch; tone: Tone }> = [];
              if (card.alwaysDest) branches.push({ branch: card.alwaysDest, tone: 'office' });
              if (card.officeHours) branches.push({ branch: card.officeHours, tone: 'office' });
              if (card.afterHours) branches.push({ branch: card.afterHours, tone: 'after' });
              if (card.holidays) branches.push({ branch: card.holidays, tone: 'holiday' });
              for (const lane of card.periodLanes || []) {
                branches.push({
                  branch: lane,
                  tone: lane.unknown ? 'unknown' : lane.outsidePeriods ? 'after' : 'office',
                });
              }

              return (
                <Card key={card.id} className="overflow-hidden p-0">
                  <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
                    <span className="text-xs font-bold text-white bg-indigo-600 px-2 py-0.5 rounded">
                      ROUTE
                    </span>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
                      {card.name}
                    </h4>
                  </div>
                  <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-5 gap-y-1 border-b border-gray-200 dark:border-gray-700">
                    <span>
                      Trunks:{' '}
                      <b className="text-gray-900 dark:text-white">{card.trunksLabel}</b>
                    </span>
                    <span>
                      DIDs: <b className="text-gray-900 dark:text-white">{card.didsLabel}</b>
                    </span>
                    <span>
                      Time mode:{' '}
                      <b className="text-gray-900 dark:text-white">{card.timeConditionMode}</b>
                    </span>
                    {card.scheduleLabel ? (
                      <span>
                        Office hours:{' '}
                        <b className="text-gray-900 dark:text-white">{card.scheduleLabel}</b>
                      </span>
                    ) : null}
                  </div>

                  <div className="p-5 space-y-3">
                    <div className="mx-auto max-w-md text-center rounded-lg bg-gray-900 text-white text-sm font-semibold py-2.5 px-3">
                      Incoming call → {card.trunksLabel !== 'All / unspecified trunks' ? card.trunksLabel : card.name}
                    </div>
                    <div className="mx-auto w-0.5 h-4 bg-gray-300 dark:bg-gray-600" />
                    {(card.officeHours || card.afterHours || card.holidays) && (
                      <div className="mx-auto max-w-lg text-center rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200 text-sm font-semibold py-2.5 px-3">
                        Office hours or after hours?
                        <span className="block text-xs font-medium opacity-80 mt-0.5">
                          {card.scheduleLabel || card.timeConditionMode}
                        </span>
                      </div>
                    )}
                    {card.periodLanes && (
                      <div className="mx-auto max-w-lg text-center rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200 text-sm font-semibold py-2.5 px-3">
                        Which time period is it?
                        <span className="block text-xs font-medium opacity-80 mt-0.5">
                          {[
                            'First match from left to right',
                            card.timezoneLabel,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                    )}

                    {branches.length === 0 ? (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                        No destination configured for this route.
                      </p>
                    ) : (
                      <div
                        className={`grid grid-cols-1 gap-4 pt-2 ${
                          branches.length > 1 ? 'md:grid-cols-2' : ''
                        } ${branches.length > 2 ? 'xl:grid-cols-3' : ''}`}
                      >
                        {branches.map(({ branch, tone }) => (
                          <BranchPanel
                            key={`${card.id}-${branch.title}`}
                            branch={branch}
                            tone={tone}
                            greetings={greetings}
                          />
                        ))}
                      </div>
                    )}

                    {card.otherQueues.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                          Other queues (not on the office menu)
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {card.otherQueues.map((q) => (
                            <div
                              key={q.id}
                              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs"
                            >
                              <b className="text-primary-700 dark:text-primary-300">
                                {q.number || q.id}
                              </b>{' '}
                              <span className="text-gray-900 dark:text-white">{q.name}</span>
                              <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                {q.agents.length
                                  ? q.agents
                                      .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
                                      .join(', ')
                                  : 'No agents listed'}
                                {q.ringStrategy ? ` · ${q.ringStrategy}` : ''}
                                {q.maxWaitTime != null ? ` · up to ${q.maxWaitTime}s` : ''}
                                {q.failDestLabel || q.failDestType
                                  ? ` · failover → ${
                                      q.failDestLabel ||
                                      [q.failDestType, q.failDestValue].filter(Boolean).join(' ')
                                    }`
                                  : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })
          )}

          {report.unlinkedIvrs.length > 0 && (
            <Card>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                Other IVR menus on this PBX
              </h4>
              <p className="mt-1 mb-4 text-xs text-gray-500 dark:text-gray-400">
                The PBX did not say where some time periods go, so here are the IVRs not shown above.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {report.unlinkedIvrs.map((ivr) => (
                  <BranchPanel
                    key={ivr.id}
                    tone="after"
                    greetings={greetings}
                    branch={{
                      title: ivr.name,
                      dest: {
                        type: 'ivr',
                        value: ivr.id,
                        label: ivr.number ? `${ivr.name} (${ivr.number})` : ivr.name,
                        kind: 'ivr',
                      },
                      ivr,
                    }}
                  />
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {!loading && !report && (
        <Card>
          <p className="text-center text-gray-500 dark:text-gray-400 py-10">
            Click <strong>Generate</strong> to pull the live inbound routing configuration from the
            PBX.
          </p>
        </Card>
      )}
    </div>
  );
}
